import omit from 'lodash/omit';
import without from 'lodash/without';

import {TPeriodPickerValue} from '@edna/components';

import {TBadgeColor} from 'src/components';
import {EChannelType, EContentType, TChannelType, TItemId} from 'src/constants';
import {TCascade, TStageEditForm} from 'src/containers/Cascades/definitions';
import {TMenuButtonAction} from 'src/containers/MenuButton';
import {
  TMessageMatcher,
  TOkNotifyMessageMatchersContent,
  TPushMessageMatchersContent,
  TVariables,
  TViberMessageMatchersContent,
  TVkNotifyMessageMatchersContent,
  TWhatsAppMessageMatchersContent,
} from 'src/containers/MessageMatchers/definitions';
import {ETrafficType} from 'src/containers/Report/definitions';

import {TItem as TTagsItem} from '../Tags/definitions';

export type {TStageEditForm};

export enum EStatus {
  DRAFT = 'DRAFT',
  CANCELING = 'CANCELING',
  SCHEDULED = 'SCHEDULED',
  CANCELLED = 'CANCELLED',
  TRIGGERED = 'TRIGGERED',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
}

export enum EStatisticsCount {
  totalCount = 'totalCount',
  deliveredCount = 'deliveredCount',
  readCount = 'readCount',
  clickedCount = 'clickedCount',
  undeliveredCount = 'undeliveredCount',
}

export type TStatus = keyof typeof EStatus;

type TStatistics = {
  totalCount: number;
  deliveredCount: number;
  undeliveredCount: number;
  clickedCount?: number;
  readCount: number;
};

export type TListBroadcast = {
  id: TItemId;
  name: string;
  cascadeName: string;
  startPlannedAt: string;
  status: TStatus;
  statistics?: TStatistics;
  createdOrScheduledBy?: string;
};

export type TStageStatistics = {
  stageUUID: TStageEditForm['uuid'];
  sentCount?: number;
  undeliveredCount?: number;
  clickedCount: number | null;
  deliveredCount?: number;
  readCount?: number;
};

export type TClicksStatistics = {
  stageUUID: TStageEditForm['uuid'];
  clickedCount?: number;
  buttons: {
    clickedCount?: number;
    index: number;
  }[];
};

export type TStatisticsCount = Record<keyof typeof EStatisticsCount, number | undefined | null>;

export type TBroadcastSummary = {
  statistics?: TStatisticsCount;
  stageStatistics?: TStageStatistics[];
  shortLinkStageStatistics?: TClicksStatistics[];
};

export type TBroadcast = {
  id?: TItemId;
  name: string;
  status: TStatus;
  statusAt?: string;
  updatedAt?: string;
  cascadeTemplateId?: TCascade['id'];
  subjectId?: number;
  startPlannedAt?: string;
  dayTimePeriod: {
    start: string;
    end: string;
    timezoneDependent: boolean;
  };
  messageSpeed: number;
  tagQueryId?: number;
  comment?: string;
  timeZone?: string;
  contents?: TBroadcastContent[];
  flowIntegration?: boolean;
  flowId?: string;
  additionalProperties?: {
    uploadId?: number;
  };
  includeUnsubscribed?: boolean;
  createdOrScheduledBy?: string;
  summary?: TBroadcastSummary;
  // WhatsApp
  useMarketingMessagesApi?: boolean;
  messageActivitySharing?: boolean;
};

export type TFormData = {
  id?: TBroadcast['id'];
  name?: string;
  statusAt?: string;
  messageSpeed?: number;
  dayTimePeriod?: {
    start: string;
    end: string;
    timezoneDependent: boolean;
  } | null;
  startNow: boolean;
  startPlannedAtDate?: Date | null;
  startPlannedAtTime?: string | null;
  isShowSubjectContent: boolean;
  cascadeTemplateId?: TCascade['id'] | null;
  subjectId?: number | null;
  contents: TBroadcastContentForm[];
  tagQueryId: number | null;
  flowIntegration?: boolean;
  flowId?: string;
  comment?: string;
  timeZone?: string;
  excludeUnsubscribed: boolean;
  additionalProperties?: {
    uploadId?: number;
  };
};

export enum EFormStep {
  SETTINGS = 'SETTINGS',
  AUDIENCE = 'AUDIENCE',
  CONTENT = 'CONTENT',
  CHECK = 'CHECK',
}

export type TStep = {
  id: EFormStep;
  disabled: boolean;
  submitType?: ESubmitType;
};

export type TTime = {
  timestamp: number;
  synchronized: number;
};

export type TBroadcastContentHolder = TMessageMatcher['content'] & {
  attachment?: TFile | null;
  caption?: string | null;
  action?: string | null;
  shouldCollectClicks?: boolean;
};

export type TBroadcastContent = {
  cascadeStageUUID: string;
  channelType: TChannelType;
  contentType: EContentType;
  contentHolder: TBroadcastContentHolder;
  messageMatcherId?: TBroadcast['id'] | null;
  // WhatsApp
  useMarketingMessagesApi?: boolean;
  messageActivitySharing?: boolean;
};

export type TCommonForm = {
  cascadeStageUUID: string | null;
  recipient?: string | null;
  contentType?: EContentType | null;
};

export type TSMSForm = TCommonForm & {
  channelType: EChannelType.SMS;
  messageMatcherId: TBroadcast['id'] | null;
  contentHolder: {
    text: string;
  };
  variables?: TVariables[];
};

export type TViberForm = TCommonForm & {
  channelType: EChannelType.VIBER;
  contentType: EContentType | null;
  messageMatcherId?: TBroadcast['id'] | null;
  contentHolder: TViberMessageMatchersContent;
};

export type TWhatsAppForm = TCommonForm & {
  channelType: EChannelType.WHATSAPP;
  messageMatcherId?: TBroadcast['id'] | null;
  language?: string;
  contentHolder: TWhatsAppMessageMatchersContent;
  variables?: TVariables[];
  useMarketingMessagesApi?: boolean;
  messageActivitySharing?: boolean;
};

export type TPushForm = TCommonForm & {
  channelType: EChannelType.PUSH;
  messageMatcherId?: TBroadcast['id'] | null;
  contentHolder: TPushMessageMatchersContent;
};

export type TVKNotifyForm = TCommonForm & {
  channelType: EChannelType.VK_NOTIFY;
  messageMatcherId?: TBroadcast['id'] | null;
  contentHolder: TVkNotifyMessageMatchersContent;
};

export type TOkNotifyForm = TCommonForm & {
  channelType: EChannelType.OK_NOTIFY;
  messageMatcherId?: TBroadcast['id'] | null;
  contentHolder: TOkNotifyMessageMatchersContent;
};

export type TMaxForm = TCommonForm & {
  channelType: EChannelType.MAX;
  messageMatcherId?: TBroadcast['id'] | null;
  contentHolder: {
    text: string;
    document?: TFile | null;
    attachment?: TFile | null;
  };
};

export type TBroadcastContentForm =
  | TSMSForm
  | TViberForm
  | TWhatsAppForm
  | TPushForm
  | TVKNotifyForm
  | TOkNotifyForm
  | TMaxForm;

export type TFilters = {
  filter?: string;
  period?: TPeriodPickerValue;
  channelTypes?: Record<TChannelType, boolean>;
  statuses?: Record<EStatus, boolean>;
  trafficTypes?: Record<ETrafficType, boolean>;
  cascadeTemplateIds?: number[];
  subjectIds?: number[];
  createdOrScheduledBy?: number[];
  isCommentFilled?: boolean;
  tagIds?: TTagsItem[];
  queryParams?: {
    statistics: string;
  };
};

export const START_PLANNED_AT_CORRECTION = 180_000;

export const colorHash: Record<TStatus, TBadgeColor> = {
  DRAFT: 'BLUE',
  SCHEDULED: 'YELLOW',
  TRIGGERED: 'GREEN',
  RUNNING: 'GREEN',
  CANCELING: 'RED',
  CANCELLED: 'RED',
  COMPLETED: 'GREEN',
};

export const MESSAGE_SPEED_DEFAULT = 5;

export const defaultInitialValues: TFormData = {
  name: '',
  messageSpeed: MESSAGE_SPEED_DEFAULT,
  isShowSubjectContent: true,
  cascadeTemplateId: null,
  subjectId: null,
  tagQueryId: null,
  contents: [],
  startNow: true,
  excludeUnsubscribed: true,
};

export const ONE_MINUTE_IN_SECONDS = 60;
export const FIFTEEN_MINUTES_IN_SECONDS = 15 * ONE_MINUTE_IN_SECONDS;

export const actionsHash: Record<TStatus, TMenuButtonAction[]> = {
  DRAFT: ['EDIT', 'DELETE', 'COPY'],
  SCHEDULED: ['EDIT', 'VIEW', 'COPY', 'CANCEL'],
  RUNNING: ['VIEW', 'CANCEL', 'COPY'],
  COMPLETED: ['VIEW', 'COPY'],
  CANCELLED: ['VIEW', 'COPY'],
  // are not described
  TRIGGERED: ['COPY'],
  CANCELING: ['COPY'],
};

const EDIT_STATUSES: TStatus[] = ['SCHEDULED', 'DRAFT'];
const VIEW_STATUSES = without(Object.values(EStatus), ...EDIT_STATUSES);

export const cancelStatusSet = new Set<TStatus>([EStatus.RUNNING]);
export const editStatusSet = new Set<TStatus>(EDIT_STATUSES);
export const viewStatusSet = new Set<TStatus>(VIEW_STATUSES);

export const filterStatuses = omit(EStatus, EStatus.TRIGGERED);

export enum ESubmitType {
  SAVE = 'SAVE',
  CONTINUE = 'CONTINUE',
  DEFAULT = 'DEFAULT',
  TEST = 'TEST',
}

export enum EBroadcastErrors {
  INSUFFICIENT_BALANCE = 'broadcast.schedule.insufficient-balance',
}

export const NO_LICENSE_CHANNELS: TChannelType[] = [
  EChannelType.PUSH,
  EChannelType.OK_NOTIFY,
  EChannelType.VK_NOTIFY,
];

export const RESTRICTED_CHANNELS: TChannelType[] = [EChannelType.MAX_BOT, EChannelType.TELEGRAM];
